import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';

const FREQS = ['Daily', 'Weekly', 'Monthly', 'Annual', 'As Needed'];
const STATUSES = ['Not Started', 'Scheduled', 'Completed'];

const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const dpart = (v) => (v ? String(v).slice(0, 10) : '');
const fmt = (v) => { const s = dpart(v); if (!s) return ''; const [y, m, d] = s.split('-'); return `${['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][+m]} ${+d}, ${y}`; };
const isOverdue = (t) => { const nd = dpart(t.next_due); return !!nd && nd < todayISO() && t.status !== 'Completed'; };
const statusMeta = (t) => {
  if (isOverdue(t)) return { label: 'Overdue', bg: '#FEF2F2', fg: '#B91C1C', dot: '#EF4444' };
  switch (t.status) {
    case 'Completed': return { label: 'Completed', bg: '#ECFDF5', fg: '#047857', dot: '#10B981' };
    case 'Scheduled': return { label: 'Scheduled', bg: '#EFF6FF', fg: '#1D4ED8', dot: '#3B82F6' };
    default:          return { label: 'Not Started', bg: '#F3F4F6', fg: '#6B7280', dot: '#9CA3AF' };
  }
};

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, []);
  return <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#111827', color: '#fff', padding: '13px 24px', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.3)', fontSize: 13, fontWeight: 500 }}>{message}</div>;
}

export default function FacilitiesMaintenance() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freq, setFreq] = useState('all');
  const [area, setArea] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // task object or {} for new
  const [toast, setToast] = useState(null);

  const load = async () => {
    try { setLoading(true); const r = await axios.get(API + '/facilities-maintenance', { headers: headers() }); setTasks(r.data); }
    catch (e) { setToast('Failed to load tasks'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markDone = async (t) => {
    try { await axios.put(`${API}/facilities-maintenance/${t.id}/complete`, {}, { headers: headers() }); setToast('Marked done — next due advanced'); load(); }
    catch (e) { setToast(e.response?.data?.error || 'Failed'); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.task}"?`)) return;
    try { await axios.delete(`${API}/facilities-maintenance/${t.id}`, { headers: headers() }); setToast('Task deleted'); load(); }
    catch (e) { setToast('Failed to delete'); }
  };
  const save = async (data) => {
    try {
      if (data.id) await axios.put(`${API}/facilities-maintenance/${data.id}`, data, { headers: headers() });
      else await axios.post(`${API}/facilities-maintenance`, data, { headers: headers() });
      setToast(data.id ? 'Task updated' : 'Task added'); setEditing(null); load();
    } catch (e) { setToast(e.response?.data?.error || 'Failed to save'); }
  };

  const areas = [...new Set(tasks.map(t => t.area).filter(Boolean))];
  const filtered = tasks.filter(t =>
    (freq === 'all' || t.frequency === freq) &&
    (area === 'all' || t.area === area) &&
    (!search || `${t.task} ${t.vendor} ${t.vendor_contact} ${t.timing} ${t.notes}`.toLowerCase().includes(search.toLowerCase()))
  );
  const overdueCount = filtered.filter(isOverdue).length;

  const pill = (active) => ({ border: `1px solid ${active ? NAVY : '#e6e9ef'}`, background: active ? NAVY : '#fff', color: active ? '#fff' : '#33415c', borderRadius: 999, padding: '6px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' });

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading maintenance calendar…</div>;

  return (
    <div>
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      {editing && <TaskModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>Maintenance Calendar</h2>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
            {filtered.length} task{filtered.length !== 1 ? 's' : ''}{overdueCount > 0 && <> · <b style={{ color: '#B91C1C' }}>{overdueCount} overdue</b></>}
          </p>
        </div>
        <button onClick={() => setEditing({})} style={{ background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>+ Add Task</button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <button style={pill(freq === 'all')} onClick={() => setFreq('all')}>All</button>
        {FREQS.map(f => <button key={f} style={pill(freq === f)} onClick={() => setFreq(f)}>{f}</button>)}
        <span style={{ width: 1, height: 22, background: '#e6e9ef', margin: '0 4px' }} />
        <button style={pill(area === 'all')} onClick={() => setArea('all')}>All areas</button>
        {areas.map(a => <button key={a} style={pill(area === a)} onClick={() => setArea(a)}>{a}</button>)}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks, vendors…"
          style={{ marginLeft: 'auto', border: '1px solid #e6e9ef', borderRadius: 8, padding: '8px 12px', fontSize: 13, minWidth: 220, outline: 'none' }} />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e6e9ef', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr>
            {['Task', 'Vendor', 'Timing', 'Last Done', 'Next Due', 'Status', ''].map((h, i) => (
              <th key={i} style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '10px 14px', borderBottom: '1px solid #e6e9ef' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {FREQS.filter(f => freq === 'all' || f === freq).map(f => {
              const rows = filtered.filter(t => t.frequency === f);
              if (!rows.length) return null;
              const od = rows.filter(isOverdue).length;
              return (
                <React.Fragment key={f}>
                  <tr><td colSpan={7} style={{ background: NAVY, color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '8px 14px' }}>
                    {f} <span style={{ fontWeight: 600, opacity: 0.8, marginLeft: 8, fontSize: 11, textTransform: 'none', letterSpacing: 0 }}>{rows.length} task{rows.length !== 1 ? 's' : ''}{od ? ` · ${od} overdue` : ''}</span>
                  </td></tr>
                  {rows.map(t => {
                    const sm = statusMeta(t); const overdue = isOverdue(t);
                    return (
                      <tr key={t.id} style={{ background: overdue ? '#fff7f7' : '#fff', borderBottom: '1px solid #f1f3f6' }}>
                        <td style={{ padding: '11px 14px', verticalAlign: 'top' }}>
                          <div style={{ fontWeight: 700, color: NAVY, lineHeight: 1.25 }}>{t.task}</div>
                          {t.area && <span style={{ display: 'inline-block', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', color: '#fff', background: t.area === 'Grounds' ? '#2f8f6b' : '#3b5ba5', borderRadius: 5, padding: '1px 7px', marginTop: 5 }}>{t.area}</span>}
                          {t.notes && <div style={{ fontSize: 11, color: '#9aa4b2', marginTop: 5, maxWidth: 300 }} title={t.notes}>{t.notes.length > 80 ? t.notes.slice(0, 80) + '…' : t.notes}</div>}
                        </td>
                        <td style={{ padding: '11px 14px', verticalAlign: 'top' }}>
                          {(t.vendor && t.vendor !== 'N/A') ? <div style={{ fontWeight: 600, color: '#33415c' }}>{t.vendor}</div> : <span style={{ color: '#c9ccd2' }}>—</span>}
                          {t.vendor_contact && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{t.vendor_contact}</div>}
                          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                            {t.vendor_phone && <a href={`tel:${t.vendor_phone}`} title={t.vendor_phone} style={{ color: NAVY, textDecoration: 'none', fontSize: 12 }}>☎ {t.vendor_phone}</a>}
                            {t.vendor_email && <a href={`mailto:${t.vendor_email}`} title={t.vendor_email} style={{ color: NAVY, textDecoration: 'none', fontSize: 12 }}>✉</a>}
                          </div>
                        </td>
                        <td style={{ padding: '11px 14px', color: '#33415c', verticalAlign: 'top' }}>{t.timing || <span style={{ color: '#c9ccd2' }}>—</span>}</td>
                        <td style={{ padding: '11px 14px', color: '#33415c', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{fmt(t.last_completed) || <span style={{ color: '#c9ccd2' }}>—</span>}</td>
                        <td style={{ padding: '11px 14px', verticalAlign: 'top', whiteSpace: 'nowrap', color: overdue ? '#B91C1C' : '#33415c', fontWeight: overdue ? 700 : 400 }}>{fmt(t.next_due) || <span style={{ color: '#c9ccd2' }}>—</span>}</td>
                        <td style={{ padding: '11px 14px', verticalAlign: 'top' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: sm.bg, color: sm.fg, whiteSpace: 'nowrap' }}>
                            <i style={{ width: 6, height: 6, borderRadius: '50%', background: sm.dot }} />{sm.label}
                          </span>
                        </td>
                        <td style={{ padding: '11px 14px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          <button onClick={() => markDone(t)} title="Mark done" style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: '#ECFDF5', color: '#047857', cursor: 'pointer', marginRight: 4 }}>✓</button>
                          <button onClick={() => setEditing(t)} title="Edit" style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: '#F5F5F7', color: '#6B7280', cursor: 'pointer', marginRight: 4 }}>✎</button>
                          <button onClick={() => remove(t)} title="Delete" style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: '#FEE2E2', color: '#991B1B', cursor: 'pointer' }}>🗑</button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No tasks match these filters.</div>}
      </div>
    </div>
  );
}

// ---- Add / Edit modal ----
function TaskModal({ initial, onClose, onSave }) {
  const [f, setF] = useState({
    id: initial.id, area: initial.area || 'Building', task: initial.task || '', vendor: initial.vendor || '',
    vendor_contact: initial.vendor_contact || '', vendor_phone: initial.vendor_phone || '', vendor_phone2: initial.vendor_phone2 || '',
    vendor_email: initial.vendor_email || '', frequency: initial.frequency || 'Daily', timing: initial.timing || '',
    last_completed: dpart(initial.last_completed), next_due: dpart(initial.next_due), status: initial.status || 'Not Started', notes: initial.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const submit = async () => { if (!f.task.trim()) return; setSaving(true); await onSave(f); setSaving(false); };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 };
  const inp = { width: '100%', padding: '9px 11px', border: '1px solid #e6e9ef', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const Field = ({ label, k, type = 'text', full }) => (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <label style={lbl}>{label}</label>
      <input type={type} value={f[k]} onChange={e => set(k, e.target.value)} style={inp} />
    </div>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 9998, padding: '40px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #F1F1F3' }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: NAVY }}>{f.id ? 'Edit task' : 'Add maintenance task'}</h3>
        </div>
        <div style={{ padding: '18px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Task / Responsibility *</label><input value={f.task} onChange={e => set('task', e.target.value)} style={inp} autoFocus /></div>
          <div><label style={lbl}>Area</label><select value={f.area} onChange={e => set('area', e.target.value)} style={inp}><option>Building</option><option>Grounds</option></select></div>
          <div><label style={lbl}>Frequency *</label><select value={f.frequency} onChange={e => set('frequency', e.target.value)} style={inp}>{FREQS.map(x => <option key={x}>{x}</option>)}</select></div>
          <Field label="Timing" k="timing" full />
          <Field label="Vendor" k="vendor" />
          <Field label="Vendor Contact" k="vendor_contact" />
          <Field label="Vendor Phone" k="vendor_phone" />
          <Field label="Vendor Phone 2" k="vendor_phone2" />
          <Field label="Vendor Email" k="vendor_email" full />
          <Field label="Last Completed" k="last_completed" type="date" />
          <Field label="Next Due" k="next_due" type="date" />
          <div><label style={lbl}>Status</label><select value={f.status} onChange={e => set('status', e.target.value)} style={inp}>{STATUSES.map(x => <option key={x}>{x}</option>)}</select></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Notes</label><textarea value={f.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 24px 20px' }}>
          <button onClick={onClose} disabled={saving} style={{ background: '#fff', border: '1px solid #e6e9ef', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: NAVY, cursor: 'pointer' }}>Cancel</button>
          <button onClick={submit} disabled={saving || !f.task.trim()} style={{ background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (saving || !f.task.trim()) ? 0.6 : 1 }}>{saving ? 'Saving…' : (f.id ? 'Save' : 'Add task')}</button>
        </div>
      </div>
    </div>
  );
}
